import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveLineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { LedgerPicker, useLedgerOptions } from '../LedgerPickers';
import { EmployeePicker, ExpenseStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ExpensesShared';

const STATUSES = ['DRAFT', 'APPROVED', 'VOID'];

const emptyLine = () => ({ category_id: '', expense_date: new Date().toISOString().slice(0, 10), amount: '', description: '' });
const emptyForm = () => ({ employee_id: '', claim_date: new Date().toISOString().slice(0, 10), lines: [emptyLine()] });

// Employee expense claims: staff submit a claim with one or more expense
// lines against a category; once approved it's paid out via a separate
// reimbursement (see ExpenseReimbursementsTab.js) - the claim itself
// doesn't post to the GL. DRAFT -> APPROVED -> VOID (void also covers
// rejection); approve/void run as checker, create as maker. Category
// options come from expenses/categories (Expenses page's Categories tab).
const ExpenseClaimsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { options: categoryOptions } = useLedgerOptions('expense-categories', () =>
    ledgerAPI.getExpenseCategories({ limit: 100 }).then((res) =>
      unwrapList(res).map((c) => ({ value: c.category_id ?? c.id, label: c.category_name }))
    )
  );

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const categoryLabel = (id) => categoryOptions.find((o) => String(o.value) === String(id))?.label || id;

  const setLine = (index, key, value) => {
    setValues((p) => ({ ...p, lines: p.lines.map((l, i) => (i === index ? { ...l, [key]: value } : l)) }));
  };
  const addLine = () => setValues((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (index) => setValues((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== index) }));

  const linesTotal = values.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const submitCreate = async () => {
    if (!values.employee_id) {
      showSnackbar('Employee is required', 'error');
      return;
    }
    const lines = values.lines
      .filter((l) => l.category_id || l.amount)
      .map((l) => ({
        category_id: Number(l.category_id),
        expense_date: l.expense_date,
        amount: Number(l.amount),
        ...(l.description?.trim() ? { description: l.description.trim() } : {}),
      }));
    if (lines.length < 1) {
      showSnackbar('At least one expense line is required', 'error');
      return;
    }
    if (lines.some((l) => !l.category_id || !(l.amount > 0))) {
      showSnackbar('Each line needs a category and an amount greater than zero', 'error');
      return;
    }
    const payload = {
      employee_id: Number(values.employee_id),
      claim_date: values.claim_date,
      lines,
    };
    try {
      setSaving(true);
      await ledgerAPI.createExpenseClaim(payload);
      showSnackbar('Claim created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getExpenseClaim(row.claim_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch claim'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidExpenseClaim(voidDlg.item.claim_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Claim rejected/voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailLines = Array.isArray(detail?.lines) ? detail.lines : [];

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.EXPENSES}
        title="Expense Claims"
        subtitle="Staff expense claims, itemised by category"
        addLabel="New Claim"
        idKey="claim_id"
        searchPlaceholder="Search claim #, employee..."
        emptyMessage="No expense claims found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getExpenseClaims(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'employee_id', label: 'Employee', picker: EmployeePicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Claim date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'total' },
        ]}
        columns={[
          { label: 'Claim #', render: (c) => <strong>{c.claim_number || `CLM-${c.claim_id}`}</strong> },
          { label: 'Employee', render: (c) => c.employee?.name || `Employee ${c.employee_id}` },
          { label: 'Date', render: (c) => fmtDate(c.claim_date) },
          { label: 'Total', render: (c) => formatAccountingAmount(c.total_amount), align: 'right' },
          { label: 'Status', render: (c) => <ExpenseStatusChip status={c.status} /> },
        ]}
        rowActions={(c) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(c) }];
          if (c.status === 'DRAFT' && canApprove) {
            actions.push({
              icon: ApproveIcon, title: 'Approve', color: 'primary',
              onClick: () => confirm({
                title: 'Approve claim',
                message: 'Approve this expense claim? It can then be reimbursed.',
                onConfirm: async () => {
                  try {
                    await ledgerAPI.approveExpenseClaim(c.claim_id);
                    showSnackbar('Claim approved successfully', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
            actions.push({ icon: VoidIcon, title: 'Reject / Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: c, void_reason: '' }) });
          }
          if (c.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete claim',
                message: `Delete DRAFT claim ${c.claim_number || `CLM-${c.claim_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteExpenseClaim(c.claim_id);
                    showSnackbar('Claim deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Expense Claim</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <EmployeePicker value={values.employee_id} onChange={(id) => setValues((p) => ({ ...p, employee_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Claim Date" value={values.claim_date}
                onChange={(e) => setValues((p) => ({ ...p, claim_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" gutterBottom>Expense lines</Typography>
          {values.lines.map((l, i) => (
            <Stack key={i} direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
              <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
                <LedgerPicker label="Category" value={l.category_id} onChange={(id) => setLine(i, 'category_id', id)} options={categoryOptions} />
              </Box>
              <TextField
                size="small" type="date" label="Expense Date" value={l.expense_date}
                onChange={(e) => setLine(i, 'expense_date', e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
              />
              <TextField
                size="small" type="number" label="Amount" value={l.amount}
                onChange={(e) => setLine(i, 'amount', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} sx={{ width: 130 }}
              />
              <TextField
                size="small" label="Description" value={l.description}
                onChange={(e) => setLine(i, 'description', e.target.value)}
                sx={{ flex: '1 1 180px' }}
              />
              <IconButton size="small" color="error" title="Remove line" onClick={() => removeLine(i)} disabled={values.lines.length <= 1}>
                <RemoveLineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}>Add line</Button>
          <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
            Total: {formatAccountingAmount(linesTotal)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Claim {detail?.claim_number || (detail?.claim_id ? `CLM-${detail.claim_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detail.employee?.name || `Employee ${detail.employee_id}`} - {fmtDate(detail.claim_date)}
              </Typography>
              <DataTable
                columns={['Category', 'Date', 'Description', 'Amount']}
                data={detailLines}
                loading={false}
                emptyMessage="No lines."
                renderRow={(l, i) => (
                  <TableRow key={l.line_id ?? i}>
                    <TableCell>{l.category?.category_name || categoryLabel(l.category_id)}</TableCell>
                    <TableCell>{fmtDate(l.expense_date)}</TableCell>
                    <TableCell>{l.description || '-'}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(l.amount)}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Total: <strong>{formatAccountingAmount(detail.total_amount)}</strong></Typography>
                <ExpenseStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Reject/void claim ${voidDlg.item?.claim_number || (voidDlg.item ? `CLM-${voidDlg.item.claim_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default ExpenseClaimsTab;
